import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getPathWithoutLang } from '../../constants/languages';

const LanguageWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 1rem;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const LanguageButton = styled.button`
  background: transparent;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.75rem 1rem;
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  font-weight: 600;
  font-size: ${props => props.theme.fontSizes.small};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: ${props => props.theme.transition};

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LanguageDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: white;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const LanguageOption = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: ${props => props.$isActive ? props.theme.colors.lightGray : 'transparent'};
  color: ${props => props.theme.colors.text};
  text-align: left;
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.small};
  transition: ${props => props.theme.transition};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.lightGray};
  }

  &:first-child {
    border-top-left-radius: ${props => props.theme.borderRadius};
    border-top-right-radius: ${props => props.theme.borderRadius};
  }

  &:last-child {
    border-bottom-left-radius: ${props => props.theme.borderRadius};
    border-bottom-right-radius: ${props => props.theme.borderRadius};
  }
`;

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' }
];

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    const pathWithoutLang = getPathWithoutLang(location.pathname);
    const newPath = `/${langCode}${pathWithoutLang}${location.search}${location.hash}`;

    i18n.changeLanguage(langCode);
    navigate(newPath);
    setIsOpen(false);
  };

  return (
    <LanguageWrapper>
      <LanguageButton onClick={() => setIsOpen(!isOpen)}>
        <span>{currentLang.flag}</span>
        <span>{currentLang.code.toUpperCase()}</span>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </LanguageButton>
      
      <LanguageDropdown $isOpen={isOpen}>
        {languages.map((lang) => (
          <LanguageOption
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            $isActive={lang.code === i18n.language}
          >
            <span>{lang.flag}</span>
            <span>{lang.name}</span>
          </LanguageOption>
        ))}
      </LanguageDropdown>
    </LanguageWrapper>
  );
};

export default LanguageSelector;

